<template>
  <el-dialog
    title="影像资料"
    :visible="visible"
    width="980px"
    append-to-body
    @close="close"
  >
    <div class="media-toolbar">
      <span>支持 PDF、JPG、PNG、JPEG 文件；同一栏目可上传多个文件。</span
      ><el-button
        type="success"
        size="small"
        icon="el-icon-download"
        :disabled="!allFiles.length"
        @click="downloadAll"
        >一键下载资料</el-button
      >
    </div>
    <div v-loading="loading" class="media-content">
      <section v-for="group in groups" :key="group.name" class="media-group">
        <h3>{{ group.name }}</h3>
        <el-table :data="group.items" border size="mini">
          <el-table-column label="状态" width="70" align="center"
            ><template slot-scope="scope"
              ><i
                :class="
                  scope.row.files.length
                    ? 'el-icon-success success'
                    : 'el-icon-error empty'
                "/></template
          ></el-table-column>
          <el-table-column prop="name" label="栏目" min-width="220" />
          <el-table-column label="文件" min-width="360">
            <template slot-scope="scope"
              ><div v-if="!scope.row.files.length" class="empty-text">
                暂未上传
              </div>
              <el-tag
                v-for="(file, index) in scope.row.files"
                :key="`${file.url}-${index}`"
                closable
                class="file-tag"
                @close="removeFile(scope.row, index)"
                ><a :href="file.url" target="_blank">{{
                  fileName(file.url)
                }}</a></el-tag
              ></template
            >
          </el-table-column>
          <el-table-column label="操作" width="110" align="center">
            <template slot-scope="scope"
              ><el-upload
                action=""
                :show-file-list="false"
                :http-request="options => upload(options, scope.row)"
                accept=".pdf,.jpg,.png,.jpeg"
                ><el-button type="text" :loading="scope.row.uploading"
                  >上传/新增</el-button
                ></el-upload
              ></template
            >
          </el-table-column>
        </el-table>
      </section>
    </div>
    <span slot="footer"
      ><el-button @click="close">取消</el-button
      ><el-button type="primary" :loading="saving" @click="save"
        >保存</el-button
      ></span
    >
  </el-dialog>
</template>

<script>
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  getClaimFiles,
  saveClaimFiles,
  uploadClaimFile
} from "@/api/claims-management";
import { MEDIA_TYPES } from "../constants";

export default {
  name: "ClaimMediaDialog",
  props: { visible: Boolean, orderId: [String, Number], riskType: Number },
  data() {
    return { loading: false, saving: false, types: [] };
  },
  computed: {
    groups() {
      const map = {};
      this.types.forEach(item => {
        if (!map[item.group]) map[item.group] = [];
        map[item.group].push(item);
      });
      return Object.keys(map).map(name => ({ name, items: map[name] }));
    },
    allFiles() {
      return this.types.reduce((list, item) => list.concat(item.files), []);
    }
  },
  watch: {
    visible(value) {
      if (value) this.load();
    }
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.types = MEDIA_TYPES.filter(
          item => item.risk === this.riskType
        ).map(item => ({ ...item, files: [], uploading: false }));
        const res = await getClaimFiles({ id: Number(this.orderId) });
        const files = (res.rows && res.rows.files) || res.rows || [];
        files.forEach(file => {
          const type = this.types.find(
            item => item.id === Number(file.claim_file_type)
          );
          if (type) type.files.push({ ...file });
        });
      } finally {
        this.loading = false;
      }
    },
    async upload(options, type) {
      if (!/\.(pdf|jpe?g|png)$/i.test(options.file.name)) {
        this.$message.error("仅支持 PDF、JPG、PNG、JPEG 文件");
        return;
      }
      type.uploading = true;
      try {
        const formData = new FormData();
        formData.append("fileVal", options.file);
        const res = await uploadClaimFile(formData);
        type.files.push({ url: res.data, claim_file_type: type.id });
        options.onSuccess(res);
      } catch (error) {
        options.onError(error);
      } finally {
        type.uploading = false;
      }
    },
    removeFile(type, index) {
      type.files.splice(index, 1);
    },
    async save() {
      this.saving = true;
      try {
        const file = [];
        this.types.forEach(type =>
          type.files.forEach(item =>
            file.push({ url: item.url, claim_file_type: type.id })
          )
        );
        await saveClaimFiles({ id: Number(this.orderId), file });
        this.$message.success("上传成功");
        this.$emit("changed");
        this.close();
      } finally {
        this.saving = false;
      }
    },
    async downloadAll() {
      const zip = new JSZip();
      this.$message.info("正在打包影像资料，请稍候");
      try {
        const files = await Promise.all(
          this.allFiles.map(async (item, index) => {
            const response = await fetch(item.url);
            if (!response.ok) throw new Error(item.url);
            return {
              name: `${index + 1}-${this.fileName(item.url)}`,
              blob: await response.blob()
            };
          })
        );
        files.forEach(file => zip.file(file.name, file.blob));
        saveAs(
          await zip.generateAsync({ type: "blob" }),
          `影像资料-${this.orderId}.zip`
        );
      } catch (error) {
        this.$message.error("资料打包失败，请逐个打开下载");
      }
    },
    fileName(url) {
      if (!url) return "";
      const clean = decodeURIComponent(String(url).split("?")[0]);
      return clean.substring(clean.lastIndexOf("/") + 1) || "查看文件";
    },
    close() {
      this.$emit("update:visible", false);
    }
  }
};
</script>

<style scoped>
.media-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  color: #606266;
}
.media-content {
  max-height: 600px;
  overflow-y: auto;
}
.media-group h3 {
  margin: 18px 0 10px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
}
.success {
  color: #67c23a;
  font-size: 18px;
}
.empty {
  color: #f56c6c;
  font-size: 18px;
}
.empty-text {
  color: #909399;
}
.file-tag {
  margin: 3px 6px 3px 0;
}
.file-tag a {
  color: #409eff;
  text-decoration: none;
}
</style>
